import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Avatar } from '@/ui/primitives/Avatar';
import { CloseIcon, MinimizeIcon, QuestionIcon } from '@/ui/primitives/Icon';
import { useChatActions, useChatConfig, useChatState } from '@/ui/ChatProvider';
import { selectMessages } from '@/state/chatStore';
/**
 * The Messages tab (`config.home`): the visitor's conversation, and a button
 * into the chat. The widget keeps one conversation per visitor, so the list
 * has at most one row; "Start a new chat" in the chat header replaces it.
 */
export function MessagesScreen({ onClose, closeIcon = 'close' }) {
    const { branding, copy } = useChatConfig();
    const { showView } = useChatActions();
    const assignee = useChatState((s) => s.conversation?.assignee);
    const last = useChatState((s) => {
        const messages = selectMessages(s);
        const message = [...messages].reverse().find((m) => m.content.trim());
        return message ? { text: preview(message.content), at: message.createdAt } : undefined;
    });
    const name = assignee?.name ?? branding.name;
    return (_jsxs(_Fragment, { children: [_jsxs("header", { className: "ac-screen-header", children: [_jsx("h2", { className: "ac-screen-title", children: copy.messages }), onClose && (_jsx("button", { type: "button", className: "ac-icon-btn", "aria-label": copy.headerBack, onClick: onClose, children: closeIcon === 'minimize' ? _jsx(MinimizeIcon, {}) : _jsx(CloseIcon, {}) }))] }), _jsxs("div", { className: "ac-messages", children: [last ? (_jsxs("button", { type: "button", className: "ac-conversation", onClick: () => showView('chat'), children: [_jsx(Avatar, { name: name, src: assignee?.avatarUrl ?? branding.avatarUrl }), _jsxs("span", { className: "ac-conversation-text", children: [_jsxs("span", { className: "ac-conversation-top", children: [_jsx("span", { className: "ac-conversation-name", children: name }), _jsx("span", { className: "ac-conversation-time", children: timeAgo(last.at) })] }), _jsx("span", { className: "ac-conversation-preview", children: last.text })] })] })) : (_jsx("p", { className: "ac-messages-empty", children: copy.noMessages })), _jsxs("button", { type: "button", className: "ac-ask-button", onClick: () => showView('chat'), children: [copy.askQuestion, _jsx(QuestionIcon, { size: 20 })] })] })] }));
}
/** One line of the last message, without the markdown punctuation. */
function preview(content) {
    const text = content.replace(/[#*_`>|[\]]/g, '').replace(/\s+/g, ' ').trim();
    return text.length > 90 ? `${text.slice(0, 89)}…` : text;
}
/** "now", "5m", "2h", "3d", "1mo" — the list's compact age. */
export function timeAgo(at, now = Date.now()) {
    const minutes = Math.floor((now - at) / 60_000);
    if (minutes < 1)
        return 'now';
    if (minutes < 60)
        return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24)
        return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 30)
        return `${days}d`;
    return `${Math.floor(days / 30)}mo`;
}
